// #3 Top bar: 64 px of glass over the content column. The breadcrumb in his screen names (an ordered list in a
// "breadcrumb" navigation landmark; the last crumb is the current page), *Search*, **?** for the *Process Guide*, and
// the notifications bell whose red dot marks unread notices (read to assistive technology as the bell's description).
// Each control is a native button; the icon buttons lift on hover and their names are their tooltips. Given `help`
// items (UAT plan D-2), **?** is a menu button named by `helpLabel` whose open list is headed by that name, with a
// hairline before its last item; `lead` comes before the breadcrumb (the phone's Menu) and `extra` after it (a state
// the bar keeps showing, such as recording on behalf of someone). The bar holds nothing else. The bell may open a
// popover of its own (`bellOpen`, `bellRef`, `bellControls`), and names it (aria-expanded, aria-controls) only while it is
// open with the popover's id, so the bell never points at a popover the page does not hold (UAT F-100); Search may name
// its keyboard shortcut. Given `assistant` (UAT F-146), the bar ends with the Assistant button beside the bell: a toggle
// (aria-pressed) for the assistant panel it names (aria-controls), with its shortcut named (aria-keyshortcuts) and
// shown in its tooltip, under the button.

import { useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode, type Ref } from "react";
import { Icon, IconButton, Menu, type MenuItem } from "../core";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { Tooltip } from "../lib/Tooltip";
import { useSvgId } from "../lib/useSvgId";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { ASSISTANT_LABELS, SHELL_LABELS } from "./labels";

export interface TopBarCrumb {
  /** His screen name, or the record's title. */
  label: string;
  /** A crumb that leads back (absent on the current page). */
  href?: string | undefined;
}

export interface TopBarHotspots {
  search?: Hs | undefined;
  help?: Hs | undefined;
  bell?: Hs | undefined;
  assistant?: Hs | undefined;
  /** Per crumb, by index. */
  crumbs?: readonly (Hs | undefined)[] | undefined;
}

/** The Assistant button (UAT F-146): a toggle for the assistant panel. */
export interface TopBarAssistant {
  /** The panel is open: aria-pressed. */
  open: boolean;
  onToggle: (event: MouseEvent<HTMLButtonElement>) => void;
  /** The panel's id (aria-controls); the panel stays on the page while closed, so the reference always resolves. */
  controls: string;
  /** The shortcut as aria-keyshortcuts names it ("Meta+J Control+J"). */
  keys?: string | undefined;
  /** The shortcut as the tooltip shows it on this platform (⌘J, Ctrl+J). */
  hint?: string | undefined;
  buttonRef?: Ref<HTMLButtonElement> | undefined;
}

export interface TopBarProps {
  crumbs?: readonly TopBarCrumb[] | undefined;
  onSearch?: ((event: MouseEvent<HTMLButtonElement>) => void) | undefined;
  onHelp?: ((event: MouseEvent<HTMLButtonElement>) => void) | undefined;
  onBell?: ((event: MouseEvent<HTMLButtonElement>) => void) | undefined;
  /** Unread notices: the bell's red dot. */
  unread?: boolean | undefined;
  /** The bell's popover is open (aria-expanded="true" with bellControls); absent, the bell opens nothing of its own. */
  bellOpen?: boolean | undefined;
  /** The bell's element, for the popover it opens. */
  bellRef?: Ref<HTMLButtonElement> | undefined;
  /** The id of the popover the bell opens (aria-controls), given while that popover is on the page. */
  bellControls?: string | undefined;
  /** Search's keyboard shortcut (aria-keyshortcuts, "Meta+K Control+K"). */
  searchKeys?: string | undefined;
  /** The ? menu's items; absent, ? is a plain button calling onHelp. */
  help?: readonly MenuItem[] | undefined;
  /** ?'s name and its open list's heading (default *Process Guide*). */
  helpLabel?: string | undefined;
  /** The ? menu is open (controlled); absent, it keeps its own state. */
  helpOpen?: boolean | undefined;
  onHelpOpenChange?: ((open: boolean) => void) | undefined;
  /** Before the breadcrumb: the phone's Menu button. */
  lead?: ReactNode | undefined;
  /** After the breadcrumb, before Search: a standing state the bar keeps showing. */
  extra?: ReactNode | undefined;
  /** The Assistant button after the bell (UAT F-146); absent, the bar ends with the bell. */
  assistant?: TopBarAssistant | undefined;
  hs?: TopBarHotspots | undefined;
  className?: string | undefined;
}

function CrumbLink({ crumb, hs }: { crumb: TopBarCrumb; hs?: Hs | undefined }) {
  const hot = useHs(hs);
  return <a className="mc-crumb-a" href={crumb.href} {...hot}>{crumb.label}</a>;
}

function SearchButton({ onClick, keys, hs }: { onClick?: TopBarProps["onSearch"]; keys?: string | undefined; hs?: Hs | undefined }) {
  const hot = useHs(hs);
  return (
    <button type="button" className="mc-search" aria-keyshortcuts={keys} onClick={onClick} {...hot}>
      <Icon name="search" size={16} />
      <span className="mc-search-t">{SHELL_LABELS.search}</span>
    </button>
  );
}

function Bell({ unread, open, bellRef, controls, onClick, hs }: {
  unread: boolean;
  open?: boolean | undefined;
  bellRef?: Ref<HTMLButtonElement> | undefined;
  controls?: string | undefined;
  onClick?: TopBarProps["onBell"];
  hs?: Hs | undefined;
}) {
  const hot = useHs(hs);
  const why = `${useSvgId()}-unread`;
  const shown = open === true && controls !== undefined;
  return (
    <>
      <Tooltip label={SHELL_LABELS.notifications} mode="mirror">
        <button
          ref={bellRef}
          type="button"
          className={cx("mc-ibtn", "mc-bell", unread && "mc-bell-unread")}
          aria-label={SHELL_LABELS.notifications}
          aria-describedby={unread ? why : undefined}
          aria-haspopup={open === undefined ? undefined : "dialog"}
          aria-expanded={shown ? true : undefined}
          aria-controls={shown ? controls : undefined}
          onClick={onClick}
          {...hot}
        >
          <Icon name="bell" size={18} />
          {unread ? <span className="mc-bell-dot" aria-hidden="true" /> : null}
        </button>
      </Tooltip>
      {unread ? <VisuallyHidden id={why}>{SHELL_LABELS.unread}</VisuallyHidden> : null}
    </>
  );
}

/** The Assistant button: the panel's name, pressed while the panel is open; its tooltip adds the shortcut. */
function AssistantButton({ assistant, hs }: { assistant: TopBarAssistant; hs?: Hs | undefined }) {
  const hot = useHs(hs);
  const name = ASSISTANT_LABELS.assistant;
  return (
    <Tooltip label={assistant.hint ? `${name} · ${assistant.hint}` : name} mode="mirror" placement="bottom">
      <button
        ref={assistant.buttonRef}
        type="button"
        className={cx("mc-ibtn", "mc-asst-btn", assistant.open && "mc-on")}
        aria-label={name}
        aria-pressed={assistant.open}
        aria-controls={assistant.controls}
        aria-keyshortcuts={assistant.keys}
        onClick={assistant.onToggle}
        {...hot}
      >
        <Icon name="panel" size={18} />
      </button>
    </Tooltip>
  );
}

/** ? as a menu button: named by `label`, its open list named by the same words, which the list draws as its heading. */
function HelpMenu({ items, label, open, onOpenChange, hs }: {
  items: readonly MenuItem[];
  label: string;
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  hs?: Hs | undefined;
}) {
  const listId = `${useSvgId()}-help`;
  const button = useRef<HTMLButtonElement | null>(null);
  const [own, setOwn] = useState(false);
  const [focusOn, setFocusOn] = useState<"first" | "last" | undefined>(undefined);
  const isOpen = open ?? own;
  const change = (next: boolean, focus?: "first" | "last") => {
    setFocusOn(next ? focus : undefined);
    if (open === undefined) setOwn(next);
    onOpenChange?.(next);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      change(true, event.key === "ArrowDown" ? "first" : "last");
    } else if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      change(false);
    }
  };
  return (
    <span className="mc-menu-anchor">
      <IconButton
        ref={button}
        icon="help"
        label={label}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={listId}
        onClick={() => change(!isOpen, "first")}
        onKeyDown={onKeyDown}
        hs={hs}
      />
      <Menu
        items={items}
        open={isOpen}
        onClose={() => change(false)}
        id={listId}
        anchor={button}
        label={label}
        autoFocus={focusOn}
        align="end"
        className="mc-help-menu"
      />
    </span>
  );
}

export function TopBar({
  crumbs = [], onSearch, onHelp, onBell, unread = false, bellOpen, bellRef, bellControls, searchKeys, help,
  helpLabel = SHELL_LABELS.processGuide, helpOpen, onHelpOpenChange, lead, extra, assistant, hs, className,
}: TopBarProps) {
  const last = crumbs.length - 1;
  return (
    <header className={cx("mc-topbar", className)}>
      {lead}
      <nav className="mc-crumbs" aria-label={SHELL_LABELS.breadcrumb}>
        <ol>
          {crumbs.map((crumb, index) => (
            <li key={`${crumb.label}-${index}`} className={index === last ? "mc-cur" : undefined}>
              {index === last || crumb.href === undefined
                ? <span className="mc-crumb-t" aria-current={index === last ? "page" : undefined}>{crumb.label}</span>
                : <CrumbLink crumb={crumb} hs={hs?.crumbs?.[index]} />}
              {index < last ? <Icon name="right" size={14} /> : null}
            </li>
          ))}
        </ol>
      </nav>
      {extra}
      <SearchButton onClick={onSearch} keys={searchKeys} hs={hs?.search} />
      {help
        ? <HelpMenu items={help} label={helpLabel} open={helpOpen} onOpenChange={onHelpOpenChange} hs={hs?.help} />
        : <IconButton icon="help" label={helpLabel} onClick={onHelp} hs={hs?.help} />}
      <Bell unread={unread} open={bellOpen} bellRef={bellRef} controls={bellControls} onClick={onBell} hs={hs?.bell} />
      {assistant ? <AssistantButton assistant={assistant} hs={hs?.assistant} /> : null}
    </header>
  );
}
